import { useId, type ComponentProps } from 'preact/compat'
import { ChevronDown } from 'lucide-preact'
import { InfoPopover } from './InfoPopover'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends Omit<ComponentProps<'select'>, 'className'> {
  label?: string
  options: SelectOption[]
  error?: string
  helperText?: string
  containerClassName?: string
  className?: string
  infoText?: string
}

export function Select({
  label,
  options,
  error,
  helperText,
  containerClassName = '',
  className = '',
  id,
  disabled,
  value,
  infoText,
  ...props
}: SelectProps) {
  const selectId = id ?? useId()

  return (
    <div className={`space-y-1.5 ${containerClassName}`}>
      {label && (
        <label htmlFor={selectId} className='block text-body-sm text-gray-700'>
          {label}
        </label>
      )}

      <div className='relative'>
        <select
          id={selectId}
          disabled={disabled}
          value={value}
          className={`
            w-full px-4 py-2.5 pr-10 text-sm text-gray-900 bg-white border rounded-2xl
            appearance-none
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cencert-navy-blue-light focus:border-transparent
            ${
              error
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 hover:border-gray-400'
            }
            ${
              disabled
                ? 'bg-gray-50 text-gray-500 cursor-not-allowed opacity-50'
                : 'cursor-pointer'
            }
            ${className}
          `}
          {...props}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className='absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none'>
          <ChevronDown
            size={16}
            className={`${disabled ? 'text-gray-400' : 'text-gray-600'}`}
          />
        </div>
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      {helperText && !error && (
        <p className='text-sm text-gray-500'>{helperText}</p>
      )}
    </div>
  )
}

export function SelectWrapper({ infoText, ...props }: SelectProps) {
  if (infoText) {
    return (
      <div className='flex items-start gap-2'>
        <div className='flex-1'>
          <Select {...props} />
        </div>
        <InfoPopover infoText={infoText} placement='auto' />
      </div>
    )
  }

  return <Select {...props} />
}
